import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function IRRCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Finance", url: "https://calclab.app/finance" },
      { name: "IRR Calculator", url: "https://calclab.app/calculator/irr" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Internal Rate of Return Calculator",
      "Calculate IRR for investment profitability analysis. Free online finance calculator.",
      "https://calclab.app/calculator/irr",
      lang
    )
  ]);
  const [cashFlows, setCashFlows] = useState("");
  const [result, setResult] = useState<{ irr: number; interpretation: string } | null>(null);

  const calculateNPV = (rate: number, flows: number[]) => {
    return flows.reduce((sum, flow, index) => {
      return sum + flow / Math.pow(1 + rate, index);
    }, 0);
  };

  const calculate = () => {
    const flows = cashFlows.split(",").map(cf => parseFloat(cf.trim()));

    if (flows.some(isNaN) || flows.length < 2) {
      toast.error("Please enter at least 2 valid cash flows");
      return;
    }

    // Newton-Raphson method to find IRR
    let rate = 0.1; // Initial guess
    const maxIterations = 1000;
    const tolerance = 0.00001;

    for (let i = 0; i < maxIterations; i++) {
      const npv = calculateNPV(rate, flows);
      const npv1 = calculateNPV(rate + tolerance, flows);
      const derivative = (npv1 - npv) / tolerance;
      
      const newRate = rate - npv / derivative;
      
      if (Math.abs(newRate - rate) < tolerance) {
        rate = newRate;
        break;
      }
      rate = newRate;
    }

    const irrPercent = rate * 100;
    const interpretation = irrPercent > 0 
      ? "Positive return on investment" 
      : irrPercent < 0 
      ? "Negative return on investment" 
      : "Break-even investment";

    setResult({ irr: irrPercent, interpretation });
    toast.success("IRR calculated!");
  };

  const reset = () => {
    setCashFlows("");
    setResult(null);
  };

  return (
    <PageLayout>
      <Card>
        <CardHeader>
          <CardTitle>Internal Rate of Return Calculator</CardTitle>
          <CardDescription>
            Calculate Internal Rate of Return for investment profitability
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="flows">Cash Flows (comma-separated, starting with initial investment as negative)</Label>
            <Input
              id="flows"
              type="text"
              placeholder="-100000, 30000, 40000, 50000, 60000"
              value={cashFlows}
              onChange={(e) => setCashFlows(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              First value should be negative (initial investment), followed by periodic returns
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-lg break-all overflow-wrap-anywhere">
                  <strong>IRR:</strong> {result.irr.toFixed(2)}%
                </p>
                <CopyResultButton value={result.irr.toFixed(2)} />
              </div>
              <p className="text-sm">
                <strong>Interpretation:</strong> {result.interpretation}
              </p>
            </div>

            <CalculatorSuperCalcCTA config={calculatorCTAs["irr"]} trigger={result?.irr} />
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
